import { useMemo, useEffect } from "react";
import { BrowserRouter, Routes, Route, useNavigate } from "react-router-dom";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { setForbiddenHandler } from "./lib/api";

import { AuthContext, useAuthState, useAuth } from "./hooks/useAuth";
import { RequireAuth } from "./components/auth/RequireAuth";
import { RequireGroup, RequireSuperAdminOrBizAdmin } from "./components/auth/RequireGroup";
import { RequirePageAccess } from "./components/auth/RequirePageAccess";
import { SmartRedirect } from "./components/auth/SmartRedirect";
import { AppShell } from "./components/layout/AppShell";

// Pages
import LoginPage from "./features/auth/LoginPage";
import ChangePasswordPage from "./features/auth/ChangePasswordPage";
import UnauthorizedPage from "./pages/UnauthorizedPage";
import LandingPage from "./pages/LandingPage";
import NotFoundPage from "./pages/NotFoundPage";

// Employee pages
import EmployeeDashboard from "./features/employee/dashboard/EmployeeDashboard";
import PayslipPage from "./features/employee/payslip/PayslipPage";
import PayslipDetail from "./features/employee/payslip/PayslipDetail";
import LoanPage from "./features/employee/loans/LoanPage";
import LeavePage from "./features/employee/leave/LeavePage";
import CoePage from "./features/employee/coe/CoePage";
import TasksPage from "./features/employee/tasks/TasksPage";
// Admin pages
import AdminDashboard from "./features/admin/dashboard/AdminDashboard";
import AdminPayslipPage from "./features/admin/payslip/AdminPayslipPage";
import ProcessedPayslipsPage from "./features/admin/payslip/ProcessedPayslipsPage";
import UsersPage from "./features/admin/users/UsersPage";
import AdminLoansPage from "./features/admin/loans/AdminLoansPage";
import AdminLeavePage from "./features/admin/leave/AdminLeavePage";
import AdminTasksPage from "./features/admin/tasks/AdminTasksPage";
import AttendancePage from "./features/admin/attendance/AttendancePage";
import ManageRolesPage from "./features/admin/roles/ManageRolesPage";
import ManageApprovalsPage from "./features/admin/approvals/ManageApprovalsPage";
import BusinessesPage from "./features/admin/businesses/BusinessesPage";
import ManageStorePage from "./features/store/ManageStorePage";
import WalletPage from "./features/employee/wallet/WalletPage";
import AdminWalletPage from "./features/admin/wallet/AdminWalletPage";
import CashAdvancePage from "./features/employee/cash-advance/CashAdvancePage";
import AdminCashAdvancePage from "./features/admin/cash-advance/AdminCashAdvancePage";
import AdminAdjustmentsPage from "./features/admin/adjustments/AdminAdjustmentsPage";
import LedgerPage from "./features/admin/ledger/LedgerPage";
import ReportsPage from "./features/admin/reports/ReportsPage";
import WorkAdjustmentsPage from "./features/employee/work-adjustments/WorkAdjustmentsPage";
import AdminWorkAdjustmentsPage from "./features/admin/work-adjustments/AdminWorkAdjustmentsPage";
import FinancialDashboardPage from "./features/admin/financials/FinancialDashboardPage";

// Non-production stages (UAT, dev) get no caching so testers always see fresh data.
// Production keeps a 5-minute stale window to reduce unnecessary refetches.
const isProduction = import.meta.env.VITE_APP_STAGE === "production";

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: isProduction ? 1000 * 60 * 5 : 0,
      retry: 1,
    },
  },
});


// Registers a global 403 handler so any API call that returns Forbidden will
// refresh the user's permissions and redirect them to the landing page,
// preventing a redirect loop to /unauthorized.
function ForbiddenHandlerSetup() {
  const navigate = useNavigate();
  const { refreshPermissions } = useAuth();

  useEffect(() => {
    setForbiddenHandler(async () => {
      await refreshPermissions();
      navigate("/", { replace: true });
    });
    return () => { setForbiddenHandler(null); };
  }, [navigate, refreshPermissions]);

  return null;
}

function AppRoutes() {
  const authState = useAuthState();

  const contextValue = useMemo(() => authState, [authState]);

  return (
    <AuthContext.Provider value={contextValue}>
      <BrowserRouter>
        <ForbiddenHandlerSetup />
        <Routes>
          {/* Landing page */}
          <Route path="/" element={<LandingPage />} />

          {/* Auth routes (no AppShell) */}
          <Route path="/login" element={<LoginPage />} />
          <Route path="/change-password" element={<ChangePasswordPage />} />
          <Route path="/unauthorized" element={<UnauthorizedPage />} />

          {/* Employee routes — Cognito group guard at layout level, page access per route */}
          <Route
            path="/employee"
            element={
              <RequireAuth>
                <RequireGroup group="normal_user">
                  <AppShell />
                </RequireGroup>
              </RequireAuth>
            }
          >
            <Route index element={<SmartRedirect candidates={[
              { page: "employee:dashboard",        href: "/employee/dashboard" },
              { page: "employee:payslip",          href: "/employee/payslip" },
              { page: "employee:loans",            href: "/employee/loans" },
              { page: "employee:leave",            href: "/employee/leave" },
              { page: "employee:coe",              href: "/employee/coe" },
              { page: "employee:tasks",            href: "/employee/tasks" },
              { page: "employee:wallet",           href: "/employee/wallet" },
              { page: "employee:cash-advance",     href: "/employee/cash-advance" },
              { page: "employee:work-adjustments", href: "/employee/work-adjustments" },
            ]} />} />
            <Route
              path="dashboard"
              element={
                <RequirePageAccess page="employee:dashboard">
                  <EmployeeDashboard />
                </RequirePageAccess>
              }
            />
            <Route
              path="payslip"
              element={
                <RequirePageAccess page="employee:payslip">
                  <PayslipPage />
                </RequirePageAccess>
              }
            />
            <Route
              path="payslip/:businessId/:period"
              element={
                <RequirePageAccess page="employee:payslip">
                  <PayslipDetail />
                </RequirePageAccess>
              }
            />
            <Route
              path="loans"
              element={
                <RequirePageAccess page="employee:loans">
                  <LoanPage />
                </RequirePageAccess>
              }
            />
            <Route
              path="leave"
              element={
                <RequirePageAccess page="employee:leave">
                  <LeavePage />
                </RequirePageAccess>
              }
            />
            <Route
              path="coe"
              element={
                <RequirePageAccess page="employee:coe">
                  <CoePage />
                </RequirePageAccess>
              }
            />
            <Route
              path="tasks"
              element={
                <RequirePageAccess page="employee:tasks">
                  <TasksPage />
                </RequirePageAccess>
              }
            />
            <Route
              path="wallet"
              element={
                <RequirePageAccess page="employee:wallet">
                  <WalletPage />
                </RequirePageAccess>
              }
            />
            <Route
              path="cash-advance"
              element={
                <RequirePageAccess page="employee:cash-advance">
                  <CashAdvancePage />
                </RequirePageAccess>
              }
            />
            <Route
              path="work-adjustments"
              element={
                <RequirePageAccess page="employee:work-adjustments">
                  <WorkAdjustmentsPage />
                </RequirePageAccess>
              }
            />
          </Route>

          {/* Admin routes — any authenticated user; access per page is controlled by RequirePageAccess */}
          <Route
            path="/admin"
            element={
              <RequireAuth>
                <AppShell />
              </RequireAuth>
            }
          >
            <Route index element={<SmartRedirect candidates={[
              { page: "admin:dashboard",         href: "/admin/dashboard" },
              { page: "admin:users",             href: "/admin/users" },
              { page: "admin:payslip",           href: "/admin/payslip" },
              { page: "admin:loans",             href: "/admin/loans" },
              { page: "admin:leave",             href: "/admin/leave" },
              { page: "admin:tasks",             href: "/admin/tasks" },
              { page: "admin:attendance",        href: "/admin/attendance" },
              { page: "admin:wallet",            href: "/admin/wallet" },
              { page: "admin:cash-advance",      href: "/admin/cash-advance" },
              { page: "admin:adjustments",       href: "/admin/adjustments" },
              { page: "admin:work-adjustments",  href: "/admin/work-adjustments" },
              { page: "admin:reports",           href: "/admin/reports" },
              { page: "admin:ledger",            href: "/admin/ledger" },
              { page: "admin:financials",        href: "/admin/financials" },
            ]} />} />
            <Route path="dashboard" element={<RequirePageAccess page="admin:dashboard"><AdminDashboard /></RequirePageAccess>} />
            <Route path="users" element={<RequirePageAccess page="admin:users"><UsersPage /></RequirePageAccess>} />
            <Route path="payslip" element={<RequirePageAccess page="admin:payslip"><AdminPayslipPage /></RequirePageAccess>} />
            {/* Super-admin results: period only */}
            <Route path="payslip/:period/results" element={<RequirePageAccess page="admin:payslip"><ProcessedPayslipsPage /></RequirePageAccess>} />
            {/* Business-admin results: businessId + period */}
            <Route path="payslip/:businessId/:period/results" element={<RequirePageAccess page="admin:payslip"><ProcessedPayslipsPage /></RequirePageAccess>} />
            <Route path="loans" element={<RequirePageAccess page="admin:loans"><AdminLoansPage /></RequirePageAccess>} />
            <Route path="leave" element={<RequirePageAccess page="admin:leave"><AdminLeavePage /></RequirePageAccess>} />
            <Route path="tasks" element={<RequirePageAccess page="admin:tasks"><AdminTasksPage /></RequirePageAccess>} />
            <Route path="attendance" element={<RequirePageAccess page="admin:attendance"><AttendancePage /></RequirePageAccess>} />
            <Route path="wallet" element={<RequirePageAccess page="admin:wallet"><AdminWalletPage /></RequirePageAccess>} />
            <Route path="cash-advance" element={<RequirePageAccess page="admin:cash-advance"><AdminCashAdvancePage /></RequirePageAccess>} />
            <Route path="adjustments" element={<RequirePageAccess page="admin:adjustments"><AdminAdjustmentsPage /></RequirePageAccess>} />
            <Route path="roles" element={<RequireSuperAdminOrBizAdmin><ManageRolesPage /></RequireSuperAdminOrBizAdmin>} />
            <Route path="approvals" element={<RequireGroup group="super-admin"><ManageApprovalsPage /></RequireGroup>} />
            <Route path="businesses" element={<RequireGroup group="super-admin"><BusinessesPage /></RequireGroup>} />
            <Route path="ledger" element={<RequirePageAccess page="admin:ledger"><LedgerPage /></RequirePageAccess>} />
            <Route path="reports" element={<RequirePageAccess page="admin:reports"><ReportsPage /></RequirePageAccess>} />
            <Route path="work-adjustments" element={<RequirePageAccess page="admin:work-adjustments"><AdminWorkAdjustmentsPage /></RequirePageAccess>} />
            <Route path="financials" element={<RequirePageAccess page="admin:financials"><FinancialDashboardPage /></RequirePageAccess>} />
          </Route>

          {/* Manage Store — role-based access via store:manage-store page key */}
          <Route
            path="/store"
            element={
              <RequirePageAccess page="store:manage-store">
                <AppShell />
              </RequirePageAccess>
            }
          >
            <Route index element={<ManageStorePage />} />
          </Route>

          {/* 404 Fallback */}
          <Route path="*" element={<NotFoundPage />} />
        </Routes>
      </BrowserRouter>
    </AuthContext.Provider>
  );
}

export default function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <AppRoutes />
    </QueryClientProvider>
  );
}
